/**
 * LotSmith — Dashboard home.
 *
 * Renders one of:
 *   - Loading skeletons (data fetching)
 *   - Error state (data fetch failed)
 *   - Empty state (no accounts yet)
 *   - New-FREE empty (account exists, no trades yet)
 *   - Full dashboard (metrics + chart + trades)
 */
import { Link } from "react-router-dom";

import { useDev } from "../providers/DevProvider.js";
import { MetricCard } from "../components/MetricCard.js";
import { EquityCurve } from "../components/EquityCurve.js";
import { RulesPanel } from "../components/RulesPanel.js";
import { RecentTrades } from "../components/RecentTrades.js";
import { EaStateBanner } from "../components/EaStateBanner.js";
import { DisciplineInsights } from "../components/DisciplineInsights.js";
import { EmptyState, ErrorState, LoadingMetricCard, Skeleton } from "../components/States.js";

const FIRM_LABELS: Record<string, string> = {
  FTMO: "FTMO",
  FUNDED_NEXT: "FundedNext",
  MFF: "MyForexFunds",
  FIVE_PERCENTERS: "The 5%ers",
  TOPSTEP: "TopStep",
  E8: "E8",
  FUNDING_PIPS: "Funding Pips",
  PERSONAL: "Personal",
  OTHER: "Other",
};

type EaStatus = "connected" | "idle" | "offline" | "never-installed";

export function DashboardHome() {
  const { isLoading, error, activeAccount, metrics, equity, trades, eaStatus, lastSyncAt, reload } = useDev();

  if (error) {
    return (
      <div className="dash">
        <ErrorState
          title="Couldn't load your data"
          body={error}
          cta={{ label: "Try again", onClick: reload }}
        />
      </div>
    );
  }

  if (isLoading) return <DashboardLoading />;

  if (!activeAccount) {
    return (
      <div className="dash">
        <EmptyState
          title="No accounts yet"
          body="Add a broker account to start tracking your equity, P&L, and prop firm rules."
          cta={{ label: "Add account", onClick: () => { window.location.href = "/onboarding"; } }}
        />
      </div>
    );
  }

  const hasActivity = metrics !== null && trades.length > 0;
  const firmLabel = FIRM_LABELS[activeAccount.propFirm] ?? activeAccount.propFirm;
  const guard = metrics ? getGuardSummary(metrics.dailyLossUsedPct, metrics.revengeTradesToday) : null;

  return (
    <div className="dash">
      <section className="dash-hero">
        <div className="dash-hero__main">
          <div className="dash__eyebrow">{firmLabel} · {activeAccount.phase}</div>
          <h1 className="dash__title">Overview</h1>
          <div className="dash-hero__meta">
            <span>#{activeAccount.brokerLogin}</span>
            <span>{activeAccount.currency}</span>
          </div>
        </div>
        <div className="dash-hero__side">
          <SyncIndicator status={eaStatus} lastSyncAt={lastSyncAt} />
          {guard && (
            <div className={`dash-hero__guard dash-hero__guard--${guard.tone}`}>
              <span className="dash-hero__guard-label">Guard</span>
              <span className="dash-hero__guard-value">{guard.label}</span>
            </div>
          )}
        </div>
      </section>

      {activeAccount.eaState && (
        <EaStateBanner
          state={activeAccount.eaState}
          reason={activeAccount.eaStateReason}
          since={activeAccount.eaStateUpdatedAt}
        />
      )}

      {hasActivity && metrics ? (
        <div className="metric-row">
          <MetricCard
            label="Equity"
            value={metrics.equity}
            format="usd"
            intent="info"
            delta={`${formatSigned(metrics.equityDelta)} · ${formatPct(metrics.equityDeltaPct)}`}
            deltaTone={metrics.equityDelta >= 0 ? "profit" : "loss"}
          />
          <MetricCard
            label="Daily P&L"
            value={metrics.dailyPnl}
            format="usd-signed"
            intent={metrics.dailyPnl >= 0 ? "profit" : "loss"}
            valueTone={metrics.dailyPnl >= 0 ? "profit" : "loss"}
            delta={`${metrics.tradesToday} trades · ${Math.round(metrics.winRate * 100)}% win`}
          />
          <MetricCard
            label="Daily loss left"
            value={metrics.dailyLossLeft}
            format="usd"
            intent={metrics.dailyLossUsedPct > 0.7 ? "warning" : "default"}
            delta={`${Math.round(metrics.dailyLossUsedPct * 100)}% used`}
            deltaTone={metrics.dailyLossUsedPct > 0.7 ? "warning" : "muted"}
          />
          <MetricCard
            label="To target"
            value={metrics.toTarget}
            format="usd"
            intent="default"
            delta={`${activeAccount.phase} · ${Math.round(metrics.targetProgressPct * 100)}%`}
            deltaTone="muted"
          />
          <MetricCard
            label="Discipline alerts"
            value={metrics.revengeTradesToday}
            format="raw"
            intent={metrics.revengeTradesToday > 0 ? "warning" : "profit"}
            valueTone={metrics.revengeTradesToday > 0 ? "warning" : "profit"}
            delta={`revenge-risk · ${metrics.revengeWindowMinutes}m`}
            deltaTone={metrics.revengeTradesToday > 0 ? "warning" : "muted"}
          />
        </div>
      ) : (
        <NewAccountChecklist propFirm={activeAccount.propFirm} eaStatus={eaStatus} />
      )}

      {hasActivity && (
        <>
          <div className="dash__row-2col">
            <section className="panel">
              <div className="panel__hdr">
                <h2 className="panel__title">Equity curve</h2>
              </div>
              <EquityCurve points={equity} />
            </section>

            {metrics && (
              <RulesPanel
                metrics={metrics}
                firmLabel={firmLabel}
              />
            )}
          </div>

          {metrics && <DisciplineInsights insights={metrics.disciplineInsights} />}

          <section className="panel">
            <div className="panel__hdr">
              <h2 className="panel__title">Recent trades</h2>
              <Link to="/app/trades" className="panel__action">View all</Link>
            </div>
            <RecentTrades trades={trades} />
          </section>
        </>
      )}
    </div>
  );
}

interface SyncIndicatorProps {
  status: EaStatus;
  lastSyncAt: Date | null;
}

function SyncIndicator({ status, lastSyncAt }: SyncIndicatorProps) {
  const map: Record<EaStatus, { dot: string; label: string }> = {
    connected:         { dot: "ok",     label: "EA connected" },
    idle:              { dot: "muted",  label: "EA idle" },
    offline:           { dot: "loss",   label: "EA offline" },
    "never-installed": { dot: "muted",  label: "EA not installed" },
  };
  const item = map[status];
  return (
    <div className="dash__sync">
      <span className={`dot dot--${item.dot}`} />
      {item.label}
      {lastSyncAt && (
        <span className="dash__sync-time">· {formatSyncTime(lastSyncAt)}</span>
      )}
    </div>
  );
}

interface NewAccountChecklistProps {
  propFirm: string;
  eaStatus: EaStatus;
}

function NewAccountChecklist({ propFirm, eaStatus }: NewAccountChecklistProps) {
  const eaInstalled = eaStatus === "connected" || eaStatus === "idle";
  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Get started</h2>
      </div>
      <ol className="checklist">
        <li className="checklist__item is-done">
          <span className="checklist__check" aria-hidden>✓</span>
          <span>{propFirm} account added</span>
        </li>
        <li className={`checklist__item${eaInstalled ? " is-done" : ""}`}>
          <span className="checklist__check" aria-hidden>{eaInstalled ? "✓" : "○"}</span>
          <span>
            Install <code>LotSmith.ex5</code> on MT5 and paste your device token
            {!eaInstalled && <> — <Link to="/onboarding" className="lnk">how to install</Link></>}
          </span>
        </li>
        <li className="checklist__item">
          <span className="checklist__check" aria-hidden>○</span>
          <span>Place your first trade — your equity curve will appear here.</span>
        </li>
      </ol>
    </section>
  );
}

function DashboardLoading() {
  return (
    <div className="dash">
      <div className="dash__hdr">
        <div style={{ width: 240 }}>
          <Skeleton height="14px" />
          <div style={{ height: 8 }} />
          <Skeleton height="28px" />
        </div>
      </div>
      <div className="metric-row">
        <LoadingMetricCard label="Equity" />
        <LoadingMetricCard label="Daily P&L" />
        <LoadingMetricCard label="Daily loss left" />
        <LoadingMetricCard label="To target" />
        <LoadingMetricCard label="Discipline alerts" />
      </div>
      <section className="panel">
        <div className="panel__hdr">
          <h2 className="panel__title">Equity curve</h2>
        </div>
        <div style={{ padding: 16 }}>
          <Skeleton height="220px" />
        </div>
      </section>
    </div>
  );
}

function formatSigned(n: number): string {
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatPct(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(2)}%`;
}
function formatSyncTime(d: Date): string {
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `synced ${diffSec}s ago`;
  if (diffSec < 3600) return `synced ${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `synced ${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleString();
}

function getGuardSummary(dailyLossUsedPct: number, revengeTradesToday: number): { label: string; tone: "ok" | "warning" | "danger" } {
  if (dailyLossUsedPct >= 0.9) return { label: "High risk", tone: "danger" };
  if (dailyLossUsedPct >= 0.7) return { label: "Slow down", tone: "warning" };
  if (revengeTradesToday > 0) return { label: "Watch revenge", tone: "warning" };
  return { label: "Clean", tone: "ok" };
}
